import React, { useEffect, useRef, useState } from "react";
import { Modal, Table, Input, Checkbox, Alert, Button } from "antd";
import { getDeletedUsers, restoreUser } from "../../Service/userService";
import { canAdminPerformUserAction } from "./userActionAccess";

export const canRestoreUsers = (actor) =>
  ["user.view", "user.edit", "user.invite"].every((key) =>
    canAdminPerformUserAction(actor, key),
  );

export default function RestoreUserAction({ actor, open, onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const operation = useRef(null);
  const inFlight = useRef(false);
  const allowed = canRestoreUsers(actor);
  const load = async (after = 0) => {
    setBusy(true);
    setError("");
    try {
      const page = await getDeletedUsers(after);
      setRows((previous) => (after ? [...previous, ...page.data] : page.data));
      setCursor(page.nextCursor);
    } catch (e) {
      setError(e?.response?.data?.message || "Unable to load deleted users.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!open || !allowed) return;
    setSelected(null);
    setConfirmed(false);
    setReason("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allowed]);
  const restore = async () => {
    if (!selected || !confirmed || !reason.trim() || busy || inFlight.current)
      return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const fingerprint = JSON.stringify([selected.UserID, reason.trim()]);
      if (operation.current?.fingerprint !== fingerprint)
        operation.current = { fingerprint, id: window.crypto.randomUUID() };
      await restoreUser({
        UserID: selected.UserID,
        SamePersonConfirmed: true,
        Reason: reason.trim(),
        OperationID: operation.current.id,
      });
      onClose?.();
      setSelected(null);
      setReason("");
      setConfirmed(false);
      operation.current = null;
      await onRestored?.();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Restoration failed. Check the selected identity and try again.",
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  if (!allowed) return null;
  return (
    <>
      <Modal
        title="Restore the original person"
        open={open}
        width={850}
        onCancel={() => !busy && onClose?.()}
        onOk={restore}
        confirmLoading={busy}
        okText="Restore and invite"
        okButtonProps={{
          disabled:
            !selected ||
            !confirmed ||
            !reason.trim() ||
            reason.length > 500 ||
            busy,
        }}
      >
        <p>
          Select the original identity only when the same person is returning. A
          recycled mailbox must not be attached to its former owner's history.
          The original role and association are retained; permissions use
          current defaults.
        </p>
        {error && <Alert type="error" message={error} />}
        <Table
          rowKey="UserID"
          dataSource={rows}
          loading={busy}
          pagination={{ pageSize: 5 }}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selected ? [selected.UserID] : [],
            onChange: (_, chosen) => {
              setSelected(chosen[0]);
              setConfirmed(false);
            },
            getCheckboxProps: (row) => ({
              disabled:
                ["Admin", "ASD", "Authorizer", "Viewer"].includes(row.Type) &&
                !(
                  actor?.IsSuperAdmin &&
                  canAdminPerformUserAction(actor, "manage_as_users.add")
                ),
            }),
          }}
          columns={[
            { title: "Name", dataIndex: "Name" },
            { title: "Email", dataIndex: "Email" },
            { title: "Role", dataIndex: "Type" },
            { title: "Association", dataIndex: "Association" },
          ]}
        />
        {cursor && (
          <Button disabled={busy} onClick={() => load(cursor)}>
            Load more deleted users
          </Button>
        )}
        <Checkbox
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        >
          I have verified that this is the same person as the original identity.
        </Checkbox>
        <Input.TextArea
          aria-label="Restoration reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          placeholder="Reason for restoration"
        />
      </Modal>
    </>
  );
}
